/**
 * Friendly, present-tense labels for the agent orchestrator's tool steps (#4).
 * The raw SSE payload carries machine names (e.g. `semantic_search`) and terse
 * summaries (e.g. "5 results"); we turn those into one plain-English line for the
 * chat's status/step display instead of leaking tool identifiers to the user.
 */

// Raw KS tool name → what the user sees while it runs.
const TOOL_LABELS: Record<string, string> = {
  semantic_search: "Searching the archive",
  find_excerpts_for_theme: "Gathering excerpts",
  find_similar: "Finding related moments",
  generate_themes: "Discovering common themes",
  autocode_seed_expand: "Expanding matches",
  list_excerpts: "Collecting excerpts",
  list_themes: "Looking up your themes",
  theme_spans: "Mapping theme moments",
  create_theme: "Drafting a theme",
  add_excerpts_to_theme: "Preparing excerpts",
};

// Turn a machine token ("find_excerpts_for_theme") into Sentence case as a
// fallback for any tool we don't have an explicit label for.
function humanize(tool: string): string {
  const words = tool.replace(/[_-]+/g, " ").trim();
  if (!words) return "Working";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Map a terse tool summary to friendly text; returns undefined when there's
// nothing worth showing (so we fall back to just the tool label).
function friendlySummary(summary?: string): string | undefined {
  const s = (summary || "").trim();
  if (!s) return undefined;
  const results = s.match(/^(\d+)\s+results?$/i);
  if (results) return `${results[1]} found`;
  if (/proposed\b.*awaiting.*confirmation/i.test(s)) return "Draft ready";
  // Keep already-human summaries (spaces, no machine tokens); drop raw ones.
  if (/\s/.test(s) && !/[_]/.test(s)) return s;
  return undefined;
}

/**
 * Build the one-line label for a tool step. Prefers the tool's friendly label,
 * appending a mapped summary when one is present (e.g. "Searching the archive —
 * 5 found"). Never returns the raw `${tool} ${status}` join.
 */
export function agentStepLabel(tool: string, _status?: string, summary?: string): string {
  const base = TOOL_LABELS[tool] || (tool ? humanize(tool) : "Working");
  const extra = friendlySummary(summary);
  return extra ? `${base} — ${extra}` : base;
}
